import { Button, Typography, Tabs, Spin } from "antd";
import FilterComponent from "components/Configuration/CommonComponents/FilterComponent";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { cglFormatDate } from "utils/functions";
import { getData } from "../../../../apiServices/common";
import AppContent from "../../../../common/AppContent";
import HandleNotification from "../../../../common/Notification";
import AppPageHeader from "../../../../common/PageHeader";
import ExtraContent from "../../../Configuration/CommonComponents/ExtraContent";
import SewingToFoldingTable from "./FinishingHourlyIronTable";
import FinishingHourlyIronTable from "./FinishingHourlyIronTable";
import { constructTitle } from "../helpers";

const HourlyIron = (props) => {
  const { type } = props;
  const location = useLocation();
  const routerPath = location ? location.pathname : null;
  const navigate = useNavigate();
  const filterName = `finishing-hourly-${type}`;

  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [page, setPage] = useState(1);
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [filterValues, setFilterValues] = useState({});
  const [activeTab, setActiveTab] = useState("assort");
  const [loading, setLoading] = useState(false);

  const { Title } = Typography;

  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  useEffect(() => {
    setFilterValues({});
    setPage(1);
  }, [routerPath, type]);

  const onFilterChange = (values) => {
    setPage(1);
    setFilterValues(values || {});
  };

  const fetchData = useCallback(
    async (filterValues) => {
      setLoading(true);
      const query = `/api/digitalization/hourly-${type}-list`;
      const foldingType = activeTab === "assort" ? "Assort Size" : "Solid";
      const bodyData = {
        page: filterValues?.page ?? page,
        per_page: filterValues?.per_page || 10,
        folding_type: foldingType,
        ...(filterValues?.finishing_line_id && {
          finishing_line_id: filterValues?.finishing_line_id,
        }),
        ...(filterValues?.style_id && { style_id: filterValues?.style_id }),
        ...(filterValues?.sub_style_id && {
          sub_style_id: filterValues?.sub_style_id,
        }),
        ...(filterValues?.original_po && {
          original_po: filterValues?.original_po,
        }),
        ...(filterValues?.po_map_id && {
          po_map_id: filterValues?.po_map_id,
        }),
        ...(filterValues?.entry_date && {
          entry_date: filterValues?.entry_date,
        }),
      };

      const response = await getData(query, false, bodyData);

      if (response && response?.data?.code === 200) {
        setTableData(response?.data?.data?.data);
        setPageSize(response?.data?.data?.per_page);
        setTotal(response?.data?.data?.total);
      } else {
        HandleNotification("error", "Error", "Something went wrong");
      }
      setLoading(false);
    },
    [page, type, activeTab],
  );

  useEffect(() => {
    const value = {
      ...filterValues,
      page: page,
    };
    fetchData(value);
  }, [refresh, page, pageSize, filterValues, type, activeTab]);

  const onPageChange = (newPage) => {
    setPage(newPage);
  };

  const handleEdit = (event, record) => {
    const params = Object.fromEntries(
      Object.entries({
        entry_date: record?.entry_date || "",
        finishing_line_id: record?.finishing_line_id || "",
        supervisor_id: record?.supervisor_id || "",
        style_id: record?.style_id || "",
        sales_contract_id: record?.sales_contract_id || "",
        buying_office_id: record?.buying_office_id || null,
        buyer_id: record?.buyer_id || null,
        sub_contract_factory_id: record?.sub_contract_factory_id || null,
        sub_style_id: record?.sub_style_id || null,
        po_map_id: record?.po_map_id || null,
        color_id: record?.color_id || "",
        size_id: record?.size_id || "",
        frp_id: record?.frp_id || null,
        operator_id: record?.operator_id || null,
        entry_time: record?.hour_time_range_from || record?.hour_time_range_to,
        hour_time_range_from: record?.hour_time_range_from || null,
        hour_time_range_to: record?.hour_time_range_to || null,
        hour: record?.hour || null,
      }).filter(([_, value]) => value !== null && value !== ""),
    );

    const searchParams = new URLSearchParams(params).toString();
    const foldingType = activeTab === "assort" ? "Assort Size" : "Solid";

    navigate(`/digitalization/finishing/hourly-${type}/edit/${record.id}?${searchParams}`, {
      state: {
        folding_type: foldingType,
        activeTab: activeTab,
      },
    });
  };

  const tableColumns = useMemo(() => {
    const baseColumns = [
      {
        title: "Entry Date",
        dataIndex: "entry_date",
        key: "entry_date",
        width: "140px",
        render: (row) => cglFormatDate(row),
      },
      {
        title: "Hour",
        dataIndex: "hour",
        key: "hour",
        width: "40px",
      },
      {
        title: "Time Range",
        key: "time_range",
        width: "140px",
        render: (row) => `${row.hour_time_range_from} - ${row.hour_time_range_to}`,
      },
      {
        title: "Finishing Line",
        dataIndex: ["finishing_line_info", "name"],
        key: "finishing_line",
        width: "140px",
      },
      {
        title: "Supervisor",
        dataIndex: ["supervisor_info", "name"],
        key: "supervisor",
        width: "140px",
      },
      {
        title: "Style",
        dataIndex: ["style_info", "style_no"],
        key: "style",
        width: "100px",
      },
      {
        title: "Factory",
        dataIndex: ["sub_contract_factory_info", "name"],
        key: "sub_contract_factory_name",
        width: "120px",
      },
      {
        title: "Sales Contract",
        dataIndex: ["sales_contract_info", "reference_no"],
        key: "sales_contract",
        width: "150px",
      },
      {
        title: "Buying Office",
        dataIndex: ["buying_office_info", "name"],
        key: "buying_office",
        width: "150px",
      },
      {
        title: "Buyer",
        dataIndex: ["buyer_info", "name"],
        key: "buyer",
        width: "150px",
      },
      {
        title: "PO",
        dataIndex: ["po_map_info", "original_po"],
        key: "po",
        width: "120px",
      },
      {
        title: "Color",
        dataIndex: ["color_info", "name"],
        key: "color",
        width: "120px",
      },
    ];

    if (activeTab === "solid") {
      baseColumns.push(
        {
          title: "Size",
          dataIndex: ["size_info", "name"],
          key: "size",
          width: "80px",
        },
        {
          title: "Qty",
          dataIndex: "quantity",
          key: "quantity",
          width: "50px",
        },
      );
    } else if (activeTab === "assort") {
      baseColumns.push({
        title: "Poly Ratio",
        dataIndex: ["folding_requirement_poly", "ratio_name"],
        key: "poly_ratio",
        width: "150px",
      });
    }

    baseColumns.push({
      title: "Action",
      key: "action",
      width: "20px",
      render: (_, record) => (
        <div>
          <Button
            style={{ marginLeft: 5 }}
            size="small"
            onClick={(event) => {
              handleEdit(event, record);
            }}
          >
            Edit
          </Button>
        </div>
      ),
    });

    return baseColumns;
  }, [activeTab]);

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        key={routerPath}
        tableData={tableData}
        filterAPI={onFilterChange}
        filterName={filterName}
        setShowFilter={setShowFilter}
        showFilter={showFilter}
      />
    ),
    [showFilter, filterName, routerPath, tableData],
  );

  const handleTabChange = (key) => {
    setActiveTab(key);
    setPage(1);
  };

  const tabItems = [
    {
      key: "assort",
      label: "Assort",
      children: (
        <Spin spinning={loading}>
          {memoizedCustomFilter}
          <FinishingHourlyIronTable
            onChange={(newPage) => {
              onPageChange(newPage);
            }}
            total={total}
            current={page}
            defaultCurrent={page}
            pageSize={pageSize}
            dataSource={tableData}
            tableColumns={tableColumns}
            pagination={true}
          />
        </Spin>
      ),
    },
    {
      key: "solid",
      label: "Solid",
      children: (
        <Spin spinning={loading}>
          {memoizedCustomFilter}
          <FinishingHourlyIronTable
            onChange={(newPage) => {
              onPageChange(newPage);
            }}
            total={total}
            current={page}
            defaultCurrent={page}
            pageSize={pageSize}
            dataSource={tableData}
            tableColumns={tableColumns}
            pagination={true}
          />
        </Spin>
      ),
    },
  ];

  const header = (
    <AppPageHeader
      extra={
        <ExtraContent
          refresh={refresh}
          setRefresh={setRefresh}
          handleAddItemOpen={fetchData}
          hideAddButton={true}
          setShowFilter={setShowFilter}
          showFilter={showFilter}
        />
      }
      title={<Title level={5}>{constructTitle(type)}</Title>}
    />
  );
  const content = (
    <Tabs activeKey={activeTab} onChange={handleTabChange} items={tabItems} tabBarStyle={{ paddingLeft: "16px" }} />
  );
  return (
    <>
      <AppContent header={header} content={content} />
    </>
  );
};

export default HourlyIron;
